import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#EAB308", "#10B981", "#6366F1", "#F43F5E", "#F59E0B", "#06B6D4", "#A855F7"];

export default function StructureModal({ open, onClose, onSaved, structures }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [rooms, setRooms] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) {
      toast.error("Inserisci il nome della struttura");
      return;
    }
    setBusy(true);
    try {
      await api.post("/structures", { name, address, rooms: parseInt(rooms) || 0, color });
      toast.success("Struttura aggiunta");
      setName(""); setAddress(""); setRooms(""); setColor(COLORS[0]);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/structures/${id}`);
      toast.success("Struttura eliminata");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            data-testid="modal-structures"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#111A2E] border border-white/15 rounded-2xl p-6 shadow-2xl max-w-lg w-full text-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold">Gestione Strutture B&B</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 max-h-52 overflow-y-auto mb-5 pr-1">
              {structures.map((s) => (
                <div key={s.id} className="flex items-center gap-3 bg-[#0E1524] border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <p className="text-xs text-slate-500 truncate">{s.address || "—"} · {s.rooms || 0} camere</p>
                  </div>
                  <button
                    data-testid={`btn-delete-structure-${s.id}`}
                    onClick={() => remove(s.id)}
                    className="text-slate-500 hover:text-rose-400 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {structures.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Nessuna struttura ancora.</p>
              )}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Aggiungi nuova struttura</p>
              <input
                data-testid="input-structure-name"
                className="w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm placeholder:text-slate-500"
                placeholder="Nome (es. Neapolis Posillipo)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="input-structure-address"
                  className="w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm placeholder:text-slate-500"
                  placeholder="Indirizzo"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                <input
                  data-testid="input-structure-rooms"
                  type="number"
                  className="w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm placeholder:text-slate-500"
                  placeholder="N° camere"
                  value={rooms}
                  onChange={(e) => setRooms(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full transition-all ${color === c ? "ring-2 ring-offset-2 ring-offset-[#111A2E] ring-white" : ""}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              <button
                data-testid="btn-add-structure"
                onClick={add}
                disabled={busy}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2.5 rounded-lg transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Aggiungi Struttura
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
